import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import { informationTypeByIdQueryOptions, informationTypeHistoryQueryOptions } from "../../api/informationTypes";
import { ClassificationScoresHistoryView } from "../../classification/ClassificationScoresHistoryView";
import { DynamicGrid } from "../../dynamicGrid";
import { DynamicHistory } from "../../dynamicHistory";
import { logUnhandled } from "../../api/logError";
import { SystemIcon3 } from "../../shared/icons";
import { Drawer } from "../../shared/Drawer";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { InformationTypeDrawerHeader } from "./InformationTypeDrawerHeader";

export const InformationTypeHistoryDrawer = () => {
  const informationTypeId = DynamicGrid.useSelectedRowId();
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const informationTypeByIdQuery = useQuery(informationTypeByIdQueryOptions(informationTypeId));
  const historyQuery = useQuery(informationTypeHistoryQueryOptions(informationTypeId));

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!informationTypeByIdQuery.data) return <Drawer.Loading />;
        const informationType = informationTypeByIdQuery.data;

        return (
          <>
            <InformationTypeDrawerHeader
              informationType={informationType}
              onClose={() => navigate(paths.informationTypesGrid())}
              status="hidden"
              activeTab="history"
            />

            {!historyQuery.data ? (
              <Drawer.Loading />
            ) : (
              <DynamicHistory.Entries
                historyEntries={historyQuery.data.history}
                renderCustomEntry={entry => {
                  switch (entry.customType) {
                    case "informationTypeName":
                      return (
                        <DynamicHistory.BeforeAndAfterEntry
                          {...entry}
                          header={t("InformationType.Grid.NameHeader")}
                          before={<DynamicHistory.SnapshotValueText>{entry.oldValue}</DynamicHistory.SnapshotValueText>}
                          after={<DynamicHistory.SnapshotValueText>{entry.newValue}</DynamicHistory.SnapshotValueText>}
                        />
                      );
                    case "informationTypeClassificationScores":
                      return (
                        <DynamicHistory.BeforeAndAfterEntry
                          {...entry}
                          header={t("History.ClassificationScores.Header")}
                          before={
                            entry.oldValue ? (
                              <ClassificationScoresHistoryView
                                scores={entry.oldValue.scores}
                                rationale={entry.oldValue.rationale}
                              />
                            ) : (
                              <DynamicHistory.EmptyValue />
                            )
                          }
                          after={
                            entry.newValue ? (
                              <ClassificationScoresHistoryView
                                scores={entry.newValue.scores}
                                rationale={entry.newValue.rationale}
                              />
                            ) : (
                              <DynamicHistory.EmptyValue />
                            )
                          }
                        />
                      );
                    case "informationTypeConnectedSystem": {
                      const connectionType = (entry.newValue ?? entry.oldValue)?.connectionType;
                      if (!connectionType)
                        throw new Error("informationTypeConnectedSystem history entry is missing connectionType");
                      return (
                        <DynamicHistory.BeforeAndAfterEntry
                          {...entry}
                          header={t(`History.Connection.${connectionType}`)}
                          before={
                            entry.oldValue && entry.oldValue.connections.length > 0 ? (
                              <DynamicHistory.MultipleValues>
                                {entry.oldValue.connections.map(connection => (
                                  <DynamicHistory.SnapshotValueText Icon={SystemIcon3} key={connection.systemId}>
                                    {connection.systemName}
                                  </DynamicHistory.SnapshotValueText>
                                ))}
                              </DynamicHistory.MultipleValues>
                            ) : (
                              <DynamicHistory.EmptyValue />
                            )
                          }
                          after={
                            entry.newValue && entry.newValue.connections.length > 0 ? (
                              <DynamicHistory.MultipleValues>
                                {entry.newValue.connections.map(connection => (
                                  <DynamicHistory.SnapshotValueText Icon={SystemIcon3} key={connection.systemId}>
                                    {connection.systemName}
                                  </DynamicHistory.SnapshotValueText>
                                ))}
                              </DynamicHistory.MultipleValues>
                            ) : (
                              <DynamicHistory.EmptyValue />
                            )
                          }
                        />
                      );
                    }
                    case "archived":
                      return entry.newValue ? (
                        <DynamicHistory.ArchivedEntry createdAt={entry.createdAt} createdBy={entry.createdBy} />
                      ) : (
                        <DynamicHistory.UnarchivedEntry createdAt={entry.createdAt} createdBy={entry.createdBy} />
                      );
                    default:
                      return logUnhandled(entry, "custom entry");
                  }
                }}
              />
            )}
          </>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};
